/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useState, useEffect, useRef} from 'react';

import PropTypes from 'prop-types';
import './RangeSlider.scss'

function RangeSlider(props) {
    const [value, setValue] = useState({
        min: props.value ?  props.value.min : props.min || 0,
        max: props.value ? props.value.max : props.max || 100,
    });
    const [isDragging, setIsDragging] = useState(false);
    const [activeThumb, setActiveThumb] = useState(null);
    const sliderRef = useRef(null);

    useEffect(() => {
        setValue({
            min: props.value ?  props.value.min : props.min || 0,
            max: props.value ? props.value.max : props.max || 100,
        });
    }, [props.value, props.min, props.max]);

    const getPercentage = (val) => {
        return ((val - props.min) / (props.max - props.min)) * 100;
    };

    const getValue = (percentage) => {
        const val = props.min + (percentage / 100) * (props.max - props.min);
        return props.step ? Math.round(val / props.step) * props.step : val;
    };

    const handleMouseDown = (thumb) => {
        if (props.disabled) return;
        setIsDragging(true);
        setActiveThumb(thumb);
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !sliderRef.current) return;
        
        const rect = sliderRef.current.getBoundingClientRect();
        const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
        const newValue = getValue(percentage);
        
        if (activeThumb === 'min') {
            const newMin = Math.min(newValue, value.max);
            setValue(prev => ({ ...prev, min: newMin }));
            props.onChangeComplete({ min: newMin, max: value.max });
        } else if (activeThumb === 'max') {
            const newMax = Math.max(newValue, value.min);
            setValue(prev => ({ ...prev, max: newMax }));
            props.onChangeComplete({ min: value.min, max: newMax });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setActiveThumb(null);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, activeThumb, value]);

    return (
        <div style={props.styles} className="rangeSliderContainer">
            {props.name && (
                <div className="rangeSliderLabel">
                    <div className="rangeSliderNameLabel">{props.name}</div>
                    <div className="rangeSliderMinMaxLabel">{value.min} - {value.max}</div>
                </div>
            )}
            <div className="custom-range-slider" ref={sliderRef}>
                <div className="range-track">
                    <div 
                        className="range-fill" 
                        style={{
                            left: `${getPercentage(value.min)}%`,
                            width: `${getPercentage(value.max) - getPercentage(value.min)}%`
                        }}
                    />
                    <div
                        className="range-thumb range-thumb-min"
                        style={{ left: `${getPercentage(value.min)}%` }}
                        onMouseDown={() => handleMouseDown('min')}
                    />
                    <div
                        className="range-thumb range-thumb-max"
                        style={{ left: `${getPercentage(value.max)}%` }}
                        onMouseDown={() => handleMouseDown('max')}
                    />
                </div>
            </div>
        </div>
    );
}

RangeSlider.propTypes = {
    name: PropTypes.string,
    disabled: PropTypes.bool,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number,
    value: PropTypes.shape({
            min: PropTypes.number.isRequired,
            max: PropTypes.number.isRequired,
    }),
    onChangeComplete: PropTypes.func.isRequired,
};

export default RangeSlider;
